<template>
  <div class="articleDetail">
    <div v-if="article" class="articleDetail__inner">
      <div class="articleDetail__container">
        <div class="articleDetail__hero">
          <CustomImage
            alt="記事FV"
            class="articleDetail__img"
            format="webp"
            sourceType="articleDetail"
            :is-pc="isPc"
            :src="article.mainVisual?.url"
          />
          <div class="articleDetail__category-date">
            <span class="articleDetail__category">
              {{ article.category?.name }}
            </span>
            <div class="articleDetail__date">
              <img
                src="/img/articles/icon_date.svg"
                alt=""
                height="14"
                width="14"
              />
              <span class="articleDetail__date-text"
                >{{ article.revisedAt }} 更新</span
              >
            </div>
          </div>
          <h1 class="articleDetail__title">
            {{ article.title }}
          </h1>
        </div>
        <div v-html="articleContent" class="articleDetail__content"></div>
      </div>
      <div class="articleDetail__author">
        <div class="articleDetail__author_header">
          <img
            :src="article.author?.iconUrl?.url"
            :width="article.author?.iconUrl?.width"
            :height="article.author?.iconUrl?.height"
            alt="著者画像"
            class="articleDetail__author_img"
          />
          <div>
            <p class="articleDetail__author_affiliation">
              {{ article.author?.affiliation }}
            </p>
            <p class="articleDetail__author_name">{{ article.author?.name }}</p>
          </div>
        </div>
        <p class="articleDetail__author_description">
          {{ article.author?.description }}
        </p>
      </div>
    </div>
    <div v-else class="loading"></div>
    <BreadCrumb :item-list="breadcrumbs" />
    <FooterFixedLink />
  </div>
</template>

<script setup lang="ts">
import type { BreadcrumbItem } from '~/utils/const';
import type { ArticleItemViewModel } from '~/core/presenter/Article/ArticleItemViewModel';
import { useArticleProfileCommentColor } from '~/hooks/useArticleProfileCommentColor';

definePageMeta({
  layout: 'magazine',
});

interface State {
  isBreadcrumbLoaded: boolean;
}
const route = useRoute();
const article = useState<ArticleItemViewModel>(
  'article',
  () => undefined as unknown as ArticleItemViewModel,
);
const articleContent = useState(() => '');
const breadcrumbs: Ref<BreadcrumbItem[]> = useState(() => []);
const isPc = useState<boolean>('isPc');
const state = reactive<State>({
  isBreadcrumbLoaded: false,
});
useArticleProfileCommentColor();

const setBreadcrumb = (title: string) => {
  breadcrumbs.value = [
    {
      title: 'マガジン',
      url: fullUrl('/articles'),
    },
    {
      title: title,
    },
  ];
  state.isBreadcrumbLoaded = true;
};

// TODO: サーバーサイドでやりたい
const replaceArticleContent = () => {
  // <table>を<div>で囲う
  articleContent.value = article.value.content;

  const regex = /<table\b[^>]*>(.*?)<\/table>/g;
  const matches = articleContent.value.match(regex);

  if (matches) {
    for (const match of matches) {
      const wrappedTable = `<div class="articleDetail__content__table_wrapper">${match}</div>`;
      articleContent.value = articleContent.value.replace(match, wrappedTable);
    }
  }
};

const { data, error } = await useFetch(`/api/v1/articles/${route.params.id}`);
if (error.value) {
  showError({
    message: 'ご指定のページが見つかりませんでした。',
    statusCode: 404,
    fatal: true,
  });
} else {
  article.value = data.value;

  replaceArticleContent();
  useSeoMeta({
    title: `${article.value?.title} | WARCエージェント マガジン`,
    description: article.value?.description,
    ogTitle: `${article.value?.title} | WARCエージェント マガジン`,
    ogDescription: article.value?.description,
    ogImage: article.value?.mainVisual?.url,
    ogType: 'article',
    ogSiteName: 'WARCエージェントマガジン',
    ogUrl: fullUrl(route.path),
  });
  useHead({
    link: [
      {
        rel: 'canonical',
        href: fullUrl(route.path),
      },
      {
        rel: 'preconnect',
        href: 'https://fonts.googleapis.com',
      },
      {
        rel: 'preconnect',
        href: 'https://fonts.gstatic.com',
      },
      {
        rel: 'stylesheet',
        href: 'https://fonts.googleapis.com/css2?family=Zen+Kaku+Gothic+Antique&display=swap',
      },
    ],
  });
  useJsonld({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${article.value?.title} | WARCエージェント マガジン`,
    image: [article.value.mainVisual?.url ?? ''],
    datePublished: dateToISODateJst(article.value.publishedAt ?? ''),
    dateModified: dateToISODateJst(article.value.updatedAt ?? ''),
    author:
      article.value.author?.id === 'oftij-11imlx'
        ? [
            {
              '@type': 'Person',
              name: article.value.author?.name,
              url: `https://agent.warc.jp/members/w5y0ze8i9h`,
            },
          ]
        : [
            {
              '@type': 'Person',
              name: article.value.author?.name,
            },
          ],
  });
  setBreadcrumb(article.value?.title);
}

onUnmounted(() => {
  article.value = undefined as unknown as ArticleItemViewModel;
  breadcrumbs.value = [];
});

onMounted(() => {
  const iframs = document.querySelectorAll('.iframely-embed > div');
  iframs.forEach((element, index) => {
    const shadowRoot = element.shadowRoot;
    if (shadowRoot !== null) {
      const style = document.createElement('style');
      style.textContent = `
      .body {
        border-radius: 4px;
        border: 1px solid rgba(199, 199, 204, 1);
      }
      .w {
        border-radius: 4px;
        border: 1px solid rgba(199, 199, 204, 1);
      }
      .wf {
        background-color: #fff !important;
        padding: 16px;
      }
      .thl {
        color: #000;
      }
      .td {
        color: #000;
      }
      .twt {
        color: rgba(128, 128, 128, 1);
      }
      .tx {
        color: rgba(128, 128, 128, 1);
      }
      .tm {
        color: rgba(128, 128, 128, 1);
      }
      .__wc-600 .wt {
        padding-top: 0px;
      }
      .wt {
        padding-top: 0px;
      }
      .e {
        padding-bottom: 0px !important;
      }
      ._lc._sm:not(.xd) .wc {
        min-height: 57px;
        height: 57px;
      }
      `;
      shadowRoot.appendChild(style);
    }
  });
});
</script>
